import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import FormActions from "../components/Admin/FormActions/FormActions";
import "./TransparenciaFile.css";

function UploadIcon({ size = 16, color = "#ffffff" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 16V4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 10L12 4L18 10"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 18V19C4 20.1 4.9 21 6 21H18C19.1 21 20 20.1 20 19V18"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function TransparenciaFile() {
  const navigate = useNavigate();
  const { category, id } = useParams();

  const [year, setYear] = useState("");
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [originalDocument, setOriginalDocument] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const type = id ? "edit" : "create";

  const CATEGORY_MAP = {
    statute: "estatuto",
    annualReports: "relatórios anuais",
    financialStatements: "demonstrações financeiras",
  };
  
  const categoryLabel = CATEGORY_MAP[category] || category;

  const uploadDocument = async (selectedFile) => {
    const formData = new FormData();
    formData.append("document", selectedFile);

    try {
      const response = await api.post("/media/upload-document", formData);
      setError(null);
      return response.data;
    } catch (uploadError) {
      setError(uploadError);
      return null;
    }
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    setFileName(selectedFile.name);
    setFile(selectedFile);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleSave = async () => {
    try {
      const numericYear = Number(year);
  
      if (!year || numericYear < 1900 || numericYear > 3000) {
        setError("O ano do documento é inválido!");
        return;
      }
  
      if (type === "create" && !file) {
        setError("Nenhum documento selecionado");
        return;
      }
  
      let url, downloadUrl, publicId;
  
      if (file) {
        const uploaded = await uploadDocument(file);
        if (!uploaded) {
          setError("Não foi possível fazer o upload do documento.");
          return;
        }
        url = uploaded.url;
        downloadUrl = uploaded.downloadUrl;
        publicId = uploaded.public_id;
  
        if (type === "edit" && originalDocument?.publicId) {
          await api.delete("/media/remove", {
            data: {
              public_id: originalDocument.publicId,
              resource_type: "raw",
            },
          });
        }
      } else {
        url = originalDocument.url;
        downloadUrl = originalDocument.downloadUrl;
        publicId = originalDocument.publicId;
      }
  
      const payload = {
        name: `${category}${year}`,
        year,
        category,
        url,
        downloadUrl,
        publicId,
      };
  
      if (type === "create") {
        await api.post("/transparencia/admin/documentos", payload);
      } else {
        await api.put(`/transparencia/admin/documentos/${id}`, payload);
      }
  
      setError(null);
      navigate("/admin/transparencia");
    } catch (saveError) {
      setError(saveError);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (type === "create") {
          setNotFound(false);
          return;
        }

        const response = await api.get(`/transparencia/admin/documentos/${id}`);
        const doc = response.data;
        setYear(doc.year);
        setFileName(doc.name);
        setOriginalDocument({
          url: doc.url,
          downloadUrl: doc.downloadUrl,
          publicId: doc.publicId,
        });

        setNotFound(false);
      } catch (fetchError) {
        setError(fetchError);

        if (fetchError.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    setError(null);
    fetchData();
  }, [type, category, id]);

  const errorMessage =
    error?.response?.data?.error || error?.message || error;

  return (
    <main className="transparencia-file-page">
      {loading ? (
        <p className="page-status">Carregando...</p>
      ) : notFound ? (
        <h1 className="page-status">Página não encontrada</h1>
      ) : (
        <>
          <header className="transparencia-file-header">
            <h1 className="transparencia-file-title">EDITAR SEÇÃO</h1>
            <p className="transparencia-file-subtitle">
              {type === "create" ? "Adicionar" : "Editar"} {categoryLabel}
            </p>
          </header>

          <section className="transparencia-file-field">
            <label
              className="transparencia-file-field-label"
              htmlFor="ano-documento"
            >
              Ano
            </label>

            <input
              id="ano-documento"
              type="text"
              className="transparencia-file-text-input"
              placeholder={`Ano ${categoryLabel}`}
              value={year}
              onChange={(event) => setYear(event.target.value)}
            />
          </section>

          <section className="transparencia-file-field">
            <label className="transparencia-file-field-label">
              {type === "create" ? "Adicionar arquivo" : "Trocar arquivo"} {categoryLabel}
            </label>

            <div className="transparencia-file-dropzone-border">
              <div
                className={`transparencia-file-dropzone${
                  dragOver ? " drag-over" : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <p className="transparencia-file-dropzone-text">
                  {fileName ||
                    "Arraste e solte um arquivo ou faça upload dele"}
                </p>

                <label className="transparencia-file-upload-button">
                  <UploadIcon />
                  FAÇA O UPLOAD DO ARQUIVO

                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(event) =>
                      handleFile(event.target.files?.[0])
                    }
                  />
                </label>

                <p className="transparencia-file-dropzone-hint">
                  O arquivo deve ser PDF
                </p>
              </div>
            </div>
          </section>

          {error && (
            <div style={{ textAlign: "center" }}>
              <span className="error-message">{String(errorMessage)}</span>
            </div>
          )}

          <FormActions
            onCancel={() => navigate(-1)}
            onSave={handleSave}
          />
        </>
      )}
    </main>
  );
}
